import { createSelector } from 'reselect'
import { sum } from 'lib/array/arrayUtils'
import { isLuxPlusSubscriptionItem, isSubscriptionJoinItem } from 'lib/checkout/checkoutUtils'
import { addYears, intlDateFormatter } from 'lib/datetime/dateUtils'
import { INTL_DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import { getCanRedeemLuxPlusBenefitsOnOrder, getIsEnabledLuxPlusDiscountedBookingProtection, getIsEnabledLuxPlusDiscountedCruise, getIsEnabledLuxPlusDiscountedInsurance, getIsFreePreviewLuxPlusMember } from 'luxPlus/selectors/featureToggle'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import { reselectShallowEqual } from 'lib/selector/selectorUtils'
import { checkIsExperienceGiftOfferOrderMode } from 'checkout/selectors/view/experience'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import { getCheckoutLoyaltyBurnTotalInCurrency } from 'checkout/selectors/loyalty/getCheckoutLoyaltyBurnTotals'
import getPayableAfterModifiers from 'checkout/selectors/payment/getPayableAfterModifiers'
import getCreditPayAmount from 'checkout/selectors/payment/getCreditPayAmount'
import getGiftCardPayAmount from 'checkout/selectors/payment/getGiftCardPayAmount'
import config from 'constants/config'
import { CheckoutPageId } from 'checkout/constants/pages'

export const getLuxPlusSubscriptionItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): Array<App.Checkout.LuxPlusSubscriptionItem> => items.filter(isLuxPlusSubscriptionItem),
  reselectShallowEqual,
)

export const getSubscriptionJoinItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items) => items.filter(isSubscriptionJoinItem),
)

export const isStandaloneLuxPlusSubscription = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): boolean => !!items.length && items.every(item => isLuxPlusSubscriptionItem(item) || isSubscriptionJoinItem(item)),
)

export const isLuxPlusSubscriptionInCart = createSelector(
  (state: App.State) => getLuxPlusSubscriptionItems(state),
  (items): boolean => items.length > 0,
)

export const isLuxPlusFullyPaidWithPoints = createSelector(
  (state: App.State) => isLuxPlusSubscriptionInCart(state),
  (state: App.State) => getCheckoutLoyaltyBurnTotalInCurrency(state),
  (state: App.State) => getPayableAfterModifiers(state),
  (state: App.State) => getCreditPayAmount(state),
  (state: App.State) => getGiftCardPayAmount(state),
  (
    hasLuxPlusSubscriptionInCart,
    loyaltyBurnTotalInCurrency,
    payableAfterModifiers,
    creditPayAmount,
    giftCardPayAmount,
  ): boolean => {
    if (!hasLuxPlusSubscriptionInCart || loyaltyBurnTotalInCurrency <= 0) return false
    return loyaltyBurnTotalInCurrency === payableAfterModifiers - creditPayAmount - giftCardPayAmount
  },
)

export const getHasLuxPlusOrSubscriptionInCart = createSelector(
  (state: App.State) => isLuxPlusSubscriptionInCart(state),
  (state: App.State) => getCanRedeemLuxPlusBenefitsOnOrder(state),
  (isLuxPlusSubscriptionInCart, canRedeemOnOrder): boolean =>
    !!(isLuxPlusSubscriptionInCart || canRedeemOnOrder),
)

export const shouldShowLuxPlusBackupPaymentMethod = createSelector(
  (state: App.State) => isLuxPlusSubscriptionInCart(state),
  (state: App.State) => state.auth.account.subscriberTier,
  () => config.LUXURY_PLUS_BACKUP_PAYMENT_METHOD_CHECKOUT_ENABLED,
  (hasLuxPlusSubscriptionInCart, subscriberTier, isFeatureEnabled): boolean => {
    return isFeatureEnabled && (hasLuxPlusSubscriptionInCart || subscriberTier === 'base')
  },
)

export const eligibleLuxPlusCheckoutNotGift = createSelector(
  (state: App.State) => getHasLuxPlusOrSubscriptionInCart(state),
  (state: App.State) => checkIsExperienceGiftOfferOrderMode(state),
  (state: App.State) => state.checkout.cart.isGift,
  (hasLuxPlusOrSubscriptionInCart, isGift, isExperienceGiftOfferOrderMode): boolean => hasLuxPlusOrSubscriptionInCart && (isExperienceGiftOfferOrderMode || !isGift),
)

export const eligibleLuxPlusPreviewCheckoutNotGift = createSelector(
  (state: App.State) => getIsFreePreviewLuxPlusMember(state),
  (state: App.State) => checkIsExperienceGiftOfferOrderMode(state),
  (state: App.State) => state.checkout.cart.isGift,
  (isFreePreviewLuxPlusMember, isGift, isExperienceGiftOfferOrderMode): boolean => isFreePreviewLuxPlusMember && (isExperienceGiftOfferOrderMode || !isGift),
)

export const checkoutWithMemberPrice = createSelector(
  (state: App.State) => eligibleLuxPlusCheckoutNotGift(state),
  (state: App.State) => isStandaloneLuxPlusSubscription(state),
  (eligibleLuxPlusCheckoutNotGift, isStandaloneLuxPlusSubscription): boolean => {
    return eligibleLuxPlusCheckoutNotGift && !isStandaloneLuxPlusSubscription
  },
)

export const checkoutPreviewingMemberPrice = createSelector(
  (state: App.State) => eligibleLuxPlusPreviewCheckoutNotGift(state),
  (state: App.State) => isStandaloneLuxPlusSubscription(state),
  (state: App.State) => state.router.location.pathname,
  (eligibleLuxPlusPreviewCheckoutNotGift, isStandaloneLuxPlusSubscription, pathname): boolean => {
    return eligibleLuxPlusPreviewCheckoutNotGift &&
      !isStandaloneLuxPlusSubscription &&
      pathname.endsWith(`/${CheckoutPageId.AddFlights}`)
  },
)

export const selectShouldUseInsuranceMemberPrice = createSelector(
  (state: App.State) => getIsEnabledLuxPlusDiscountedInsurance(state),
  (state: App.State) => checkoutWithMemberPrice(state),
  (isEnabledLuxPlusDiscountedInsurance, checkoutWithMemberPrice): boolean =>
    isEnabledLuxPlusDiscountedInsurance && checkoutWithMemberPrice,
)

export const selectShouldUseBookingProtectionMemberPrice = createSelector(
  (state: App.State) => getIsEnabledLuxPlusDiscountedBookingProtection(state),
  (state: App.State) => checkoutWithMemberPrice(state),
  (isEnabledLuxPlusDiscountedBookingProtection, checkoutWithMemberPrice): boolean =>
    isEnabledLuxPlusDiscountedBookingProtection && checkoutWithMemberPrice,
)

export const getShouldUseCruiseMemberPrice = createSelector(
  (state: App.State) => getIsEnabledLuxPlusDiscountedCruise(state),
  (state: App.State) => checkoutWithMemberPrice(state),
  (isEnabledLuxPlusDiscountedCruise, checkoutWithMemberPrice): boolean =>
    isEnabledLuxPlusDiscountedCruise && checkoutWithMemberPrice,
)

export const getLuxPlusSubscriptionItemView = createSelector(
  (state: App.State) => getLuxPlusSubscriptionItems(state),
  (luxPlusItems): App.WithDataStatus<Array<App.Checkout.LuxPlusSubscriptionItemView>> => {
    return {
      hasRequiredData: true,
      data: luxPlusItems.map(mapLuxPlusSubscriptionItemToView),
    }
  },
)

export const getLuxPlusSubscriptionsTotalPrice = createSelector(
  (state: App.State) => getLuxPlusSubscriptionItems(state),
  (items): number => sum(items, item => item.amount || 0),
)

export const getSubscriptionJoinItemView = createSelector(
  (state: App.State) => getSubscriptionJoinItems(state),
  (items): App.WithDataStatus<Array<App.Checkout.SubscriptionJoinItemView>> => {
    return {
      hasRequiredData: true,
      data: items.map(mapSubscriptionJoinItemToView),
    }
  },
)

export function mapLuxPlusSubscriptionItemToView(item: App.Checkout.LuxPlusSubscriptionItem): App.Checkout.LuxPlusSubscriptionItemView {
  const intl = getCurrentIntl()
  return {
    title: intl.formatMessage(
      {
        id: 'aC0ZVsG5BANup4YfJUGH',
        defaultMessage: '{programName} Membership',
        description: 'Title for LuxPlus membership subscription item in checkout',
      },
      { programName: LUXURY_PLUS.PROGRAM_NAME },
    ),
    item,
    loyalty: {
      productType: 'lux_plus',
    },
    offerId: item.offerId,
    totals: {
      price: item.amount,
      taxesAndFees: 0,
      memberPrice: 0,
      value: 0,
      memberValue: 0,
      surcharge: 0,
      extraGuestSurcharge: 0,
    },
  }
}

export function mapSubscriptionJoinItemToView(item: App.Checkout.SubscriptionJoinItem): App.Checkout.SubscriptionJoinItemView {
  const intl = getCurrentIntl()
  return {
    title: intl.formatMessage(
      {
        id: 'RNplUqMFP9SuaMmzeTpt',
        defaultMessage: '{programName} Membership Joining Fee',
        description: 'Title for LuxPlus membership joining fee item in checkout',
      },
      { programName: LUXURY_PLUS.PROGRAM_NAME },
    ),
    item,
    loyalty: {
      productType: 'lux_plus',
    },
    /** @TODO refactor to use offerId. svc-cart is using subscriptionOfferId. */
    offerId: item.subscriptionOfferId,
    totals: {
      price: item.waived ? 0 : item.amount,
      value: item.amount,
      taxesAndFees: 0,
      memberPrice: 0,
      memberValue: 0,
      surcharge: 0,
      extraGuestSurcharge: 0,
    },
  }
}

export const getLuxPlusBreakdownView = createSelector(
  (state: App.State) => getLuxPlusSubscriptionItemView(state),
  (state: App.State) => getLuxPlusSubscriptionsTotalPrice(state),
  (state: App.State) => getSubscriptionJoinItems(state),
  (subscriptionViews, totalPrice, joinViews): App.WithDataStatus<Array<App.Checkout.PriceBreakdownView>> => {
    if (subscriptionViews.data.length === 0) {
      return { hasRequiredData: subscriptionViews.hasRequiredData, data: [] }
    }

    const intl = getCurrentIntl()
    const membershipTitle = intl.formatMessage(
      {
        id: '7ClYlwOBXpLIELEWdLdZ',
        defaultMessage: '{programName} Membership',
        description: 'Title for LuxPlus membership in checkout price breakdown',
      },
      { programName: LUXURY_PLUS.PROGRAM_NAME },
    )

    const breakdownViews: Array<App.Checkout.PriceBreakdownView> = subscriptionViews.data.map(view => {
      const joinFeeItem = joinViews.find(join => join.subscriptionItemId === view.item.itemId)
      const breakdown: App.Checkout.PriceBreakdownView = {
        title: membershipTitle,
        price: totalPrice,
        memberPrice: 0,
        additionalInfoText: [],
        alwaysShowItemPrice: true,
        itemsAlwaysVisible: true,
        items: [{
          itemType: 'lux-plus-subscription',
          title: membershipTitle,
          itemId: view.item.itemId,
          additionalInfo: [
            {
              text: intl.formatMessage({
                id: 'oOpSlV18sZFrWp5_m2ir',
                defaultMessage: 'Yearly membership',
                description: 'Label indicating LuxPlus subscription is billed yearly',
              }),
            },
            {
              text: intl.formatMessage(
                {
                  id: 'kHSKs6BtgznXX9EoXBIR',
                  defaultMessage: 'Renews {renewalDate}.',
                  description: 'LuxPlus membership renewal date in the date selection or calendar picker',
                },
                { renewalDate: intlDateFormatter(addYears(new Date(), 1), INTL_DMY_CASUAL_SHORT_FORMAT) },
              ),
            },
          ],
          additionalElements: joinFeeItem?.amount ? [{
            id: 'policy',
            type: 'cancellation-policy',
            left: {
              type: 'text',
              value: intl.formatMessage({
                id: '2Bk2ZIGrALaott92Hrna',
                defaultMessage: 'Cancel renewal anytime',
                description: 'Cancellation policy for LuxPlus membership renewal',
              }),
            },
            right: {
              type: 'price',
              value: 0,
            },
          }] : [],
        }],
      }

      if (joinFeeItem) {
        if (!joinFeeItem.waived) {
          breakdown.items[0].additionalElements.push({
            id: 'joinFee',
            type: 'price-line',
            left: {
              type: 'text',
              value: intl.formatMessage({
                id: 'uVTWSeYCEZwVVXyg7TC0',
                defaultMessage: 'One-off joining fee',
                description: 'Form field label for LuxPlus one-time membership joining fee',
              }),
            },
            right: {
              type: 'price',
              value: joinFeeItem.amount,
            },
          })
        }
        if (joinFeeItem.waived && joinFeeItem.amount) {
          breakdown.items[0].additionalElements.push({
            id: 'joinFeeWaiver',
            variant: 'lux-plus',
            type: 'price-line',
            left: {
              type: 'text',
              value: intl.formatMessage({
                id: 'AJXZmVijV2_yU5ip6aLk',
                defaultMessage: 'Special offer: No joining fee',
                description: 'Label indicating LuxPlus joining fee has been waived',
              }),
            },
            right: {
              type: 'price',
              value: joinFeeItem.amount,
              strikethrough: true,
            },
          })
        }
      }

      return breakdown
    })

    return {
      hasRequiredData: true,
      data: breakdownViews,
    }
  },
)
